import * as React from "react";
import * as microsoftTeams from "@microsoft/teams-js";
import { OpenRegular } from "@fluentui/react-icons";
import {
    Body1Strong,
    Body2,
    Button,
    Caption1,
    Card,
    FluentProvider,
    makeStyles,
    mergeClasses,
    Title2,
    tokens,
} from "@fluentui/react-components";
import { getSharedContext } from "@microsoft/shifts-experiences-shared";
import { ThemeType } from "@microsoft/shifts-ui-core";
import StringsStore from "sh-strings/store";
import ThemeUtils from "sh-application/utility/ThemeUtils";
import { appViewState, launchTeamSetupPicker } from "sh-application/store";
import { getGenericEventPropertiesObject } from "sh-instrumentation";
import { InstrumentationService, shareAppToTeams } from "sh-services";
import { LandingPageMode } from "../LandingPageViewState";
import { TeamSetupPickerMode } from "../teamsetuppicker";

const LEARN_MORE_URL = "https://go.microsoft.com/fwlink/?LinkId=2367510";
const FOLLOW_SCHEDULE_URL = "https://go.microsoft.com/fwlink/?LinkId=2367138";

const SHIFTS_APP_URL = "https://teams.cloud.microsoft/l/app/42f6c1da-a241-483a-a3cc-4f5be9185951?source=shift-welcome-page";
const illustrationLight = require("sh-assets/images/onboarding_Illustration_light.svg");
const illustrationDark = require("sh-assets/images/onboarding_Illustration_dark.svg");
const createScheduleImageLight = require("sh-assets/images/create-schedule-light.svg");
const viewScheduleImageLight = require("sh-assets/images/view-schedule-light.svg");
const createScheduleImageDark = require("sh-assets/images/create-schedule-dark.svg");
const viewScheduleImageDark = require("sh-assets/images/view-schedule-dark.svg");

export interface UnknownUserWelcomeProps {
    landingPageMode: LandingPageMode;
    canCreateTeam: boolean;
}

const useStyles = makeStyles({
    root: {
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: "702px",
        backgroundColor: tokens.colorNeutralBackground1,
        overflow: "hidden"
    },
    welcomeCardAndIllustrationBlock: {
        position: "relative",
        display: "flex",
        alignItems: "center",
        width: "100%",
        height: "100%"
    },
    illustration: {
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
        objectPosition: "center",
        pointerEvents: "none"
    },
    card: {
        position: "relative",
        flex: "0 0 auto",
        width: "448px",
        maxWidth: "calc(100% - 112px)",
        padding: "58px 48px 48px 48px",
        marginLeft: "56px",
        boxSizing: "border-box",
        borderRadius: "12px",
        backgroundColor: tokens.colorNeutralBackgroundAlpha2,
        border: `1px solid ${tokens.colorNeutralStrokeOnBrand}`,
        boxShadow: "0 0 4px 0 rgba(0,0,0,0.05)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: tokens.spacingHorizontalXXL,
        // On narrow Teams windows the 448px card capped at calc(100% - 112px) with a 56px
        // left margin leaves the card (and its full-width buttons) cramped. Let it span the
        // viewport with tighter side padding so the buttons take the available width.
        "@media (max-width: 600px)": {
            width: "100%",
            maxWidth: "100%",
            marginLeft: 0,
            padding: "40px 20px 24px 20px"
        }
    },
    textBlock: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        alignSelf: "center",
        gap: "7px",
        width: "352px",
        maxWidth: "100%",
        textAlign: "center"
    },
    title: {
        color: tokens.colorNeutralForeground1,
        textAlign: "center",
        fontFamily: tokens.fontFamilyBase,
        fontSize: tokens.fontSizeHero700,
        fontStyle: "normal",
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: tokens.lineHeightHero700,
        whiteSpace: "nowrap"
    },
    body: {
        color: tokens.colorNeutralForeground1
    },
    actions: {
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        width: "100%"
    },
    createScheduleCard: {
        flexDirection: "row",
        alignItems: "center",
        gap: tokens.spacingHorizontalL,
        padding: tokens.spacingVerticalL,
        cursor: "pointer",
        borderRadius: tokens.borderRadiusLarge,
        border: `1px solid ${tokens.colorTransparentStroke}`,
        backgroundColor: tokens.colorNeutralBackground1,
        boxShadow: tokens.shadow4
    },
    createScheduleImage: {
        flex: "0 0 auto",
        width: "56px",
        height: "56px",
        objectFit: "contain"
    },
    createScheduleText: {
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        textAlign: "left"
    },
    createScheduleSubtitle: {
        color: tokens.colorNeutralForeground3
    },
    learnMoreButton: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightRegular,
        lineHeight: tokens.lineHeightBase300,
        color: tokens.colorBrandForegroundLink
    },
    actionButton: {
        display: "flex",
        height: "48px",
        padding: "5px 12px",
        justifyContent: "center",
        alignItems: "center",
        alignSelf: "stretch",
        borderRadius: tokens.borderRadiusMedium,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        backgroundColor: tokens.colorNeutralBackground1
    },
    buttonGroup: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "12px",
        alignSelf: "stretch"
    },
    // Layout only — the accent fill, hover and pressed states come from
    // Button's appearance="primary", so no color/background overrides here.
    primaryActionButton: {
        display: "flex",
        height: "48px",
        padding: "5px 12px",
        justifyContent: "center",
        alignItems: "center",
        alignSelf: "stretch",
        borderRadius: tokens.borderRadiusMedium
    },
    transparentActionButton: {
        backgroundColor: tokens.colorTransparentBackground
    }
});

const UserOnboardingWelcome: React.FC<UnknownUserWelcomeProps> = ({ landingPageMode, canCreateTeam }) => {
    const styles = useStyles();
    const strings = StringsStore().registeredStringModules.get("landingPage")!.strings;
    const { theme } = getSharedContext();
    const isDarkTheme = theme === ThemeType.Dark || theme === ThemeType.HighContrast;
    const illustration = isDarkTheme ? illustrationDark : illustrationLight;
    const createScheduleImage = isDarkTheme ? createScheduleImageDark : createScheduleImageLight;
    const viewScheduleImage = isDarkTheme ? viewScheduleImageDark : viewScheduleImageLight;

    const [currentLandingPageMode, setCurrentLandingPageMode] = React.useState(LandingPageMode.ShowUserOnboardingFLM);
    const [uiStateCanCreateTeam, setUiStateCanCreateTeam] = React.useState(true);

    // Emits a converged-onboarding event for an action the user took on the welcome page. LandingPageMode
    // is attached so each action can be sliced by which welcome variant (FLM/FLW) was on screen at the time.
    const logWelcomeAction = (action: string) => {
        InstrumentationService.logEvent(InstrumentationService.events.ConvergedOnboarding, [
            getGenericEventPropertiesObject(InstrumentationService.properties.EventType, action),
            getGenericEventPropertiesObject(InstrumentationService.properties.LandingPageMode, currentLandingPageMode),
            getGenericEventPropertiesObject(InstrumentationService.properties.OnboardingUserCanCreateTeam, canCreateTeam),
            getGenericEventPropertiesObject(InstrumentationService.properties.OnboardingPageLoadedFor, landingPageMode)
        ]);
    };

    React.useEffect(() => {
        logWelcomeAction(InstrumentationService.values.UserOnboardingWelcomePageSeen);
    }, []);

    const handleCreateSchedule = () => {
        if (landingPageMode === LandingPageMode.ShowUserOnboardingFLM || (landingPageMode === LandingPageMode.ShowUserOnboardingFLW && canCreateTeam)) {
            logWelcomeAction(InstrumentationService.values.UserOnboardingCreateScheduleClicked);
            launchTeamSetupPicker(
                appViewState().teamSetupPickerViewState,
                true /* isOnboarding */,
                TeamSetupPickerMode.NewUserOnboarding,
                true /* isUserDismissable */,
                false /* useTeamPickerPanel */
            );
        } else {
            setCurrentLandingPageMode(landingPageMode);
            setUiStateCanCreateTeam(canCreateTeam);
            logWelcomeAction(InstrumentationService.values.UserOnboardingCreateScheduleClicked);
        }
    };

    const handleViewSchedule = () => {
        setCurrentLandingPageMode(LandingPageMode.ShowUserOnboardingFLW);
        if (landingPageMode === LandingPageMode.ShowUserOnboardingFLM) {
            setUiStateCanCreateTeam(true);
        } else {
            setUiStateCanCreateTeam(canCreateTeam);
        }
        logWelcomeAction(InstrumentationService.values.UserOnboardingViewScheduleClicked);
    };

    const handleShareShiftsApp = () => {
        logWelcomeAction(InstrumentationService.values.UserOnboardingShareAppClicked);
        void shareAppToTeams({ url: SHIFTS_APP_URL });
    };

    const handleLearnMore = () => {
        logWelcomeAction(InstrumentationService.values.UserOnboardingLearnMoreClicked);
        void microsoftTeams.app.openLink(LEARN_MORE_URL);
    };

    const handleFindSchedule = () => {
        logWelcomeAction(InstrumentationService.values.UserOnboardingFindScheduleClicked);
        void microsoftTeams.app.openLink(FOLLOW_SCHEDULE_URL);
    };

    // Fluent Card makes a card with onClick focusable but does not activate it via the keyboard.
    // Trigger the handler on Enter/Space so keyboard users can activate the card.
    const handleCardKeyDown = (handler: () => void) => (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
            event.preventDefault();
            handler();
        }
    };

    const createAndViewScheduleCard = () => {
        return (
            <div className={styles.actions}>
                <Card
                    className={styles.createScheduleCard}
                    orientation="horizontal"
                    role="button"
                    aria-label={`${strings.get("onboardingCreateScheduleCardTitle")}. ${strings.get("onboardingCreateScheduleCardSubtitle")}`}
                    onClick={handleCreateSchedule}
                    onKeyDown={handleCardKeyDown(handleCreateSchedule)}
                >
                    <img
                        className={styles.createScheduleImage}
                        src={createScheduleImage}
                        alt=""
                        aria-hidden="true"
                    />
                    <div className={styles.createScheduleText}>
                        <Body1Strong>{strings.get("onboardingCreateScheduleCardTitle")}</Body1Strong>
                        <Caption1 className={styles.createScheduleSubtitle}>
                            {strings.get("onboardingCreateScheduleCardSubtitle")}
                        </Caption1>
                    </div>
                </Card>
                <Card
                    className={styles.createScheduleCard}
                    orientation="horizontal"
                    role="button"
                    aria-label={`${strings.get("onboardingViewScheduleCardTitle")}. ${strings.get("onboardingViewScheduleCardSubtitle")}`}
                    onClick={handleViewSchedule}
                    onKeyDown={handleCardKeyDown(handleViewSchedule)}
                >
                    <img
                        className={styles.createScheduleImage}
                        src={viewScheduleImage}
                        alt=""
                        aria-hidden="true"
                    />
                    <div className={styles.createScheduleText}>
                        <Body1Strong>{strings.get("onboardingViewScheduleCardTitle")}</Body1Strong>
                        <Caption1 className={styles.createScheduleSubtitle}>
                            {strings.get("onboardingViewScheduleCardSubtitle")}
                        </Caption1>
                    </div>
                </Card>
            </div>
        );
    };

    const renderTitleBlock = (landingPageMode?: LandingPageMode, canCreateTeam?: boolean) => {
        return (
            <>
                {
                    landingPageMode === LandingPageMode.ShowUserOnboardingFLM &&
                    <>
                        <Title2 className={styles.title}>
                            {strings.get("onboardingWelcomeTitle")}
                        </Title2>
                        <Body2 className={styles.body}>
                            {strings.get("onboardingWelcomeSubtitle")}
                        </Body2>
                    </>
                }
                {
                    landingPageMode === LandingPageMode.ShowUserOnboardingFLW && canCreateTeam &&
                    <>
                        <Title2 className={styles.title}>
                            {strings.get("onboardingNotOnScheduleTitle")}
                        </Title2>
                        <Body2 className={styles.body}>
                            {strings.get("onboardingNotOnScheduleSubtitle")}
                        </Body2>
                    </>
                }
                {
                    landingPageMode === LandingPageMode.ShowUserOnboardingFLW && !canCreateTeam &&
                    <>
                        <Title2 className={styles.title}>
                            {strings.get("onboardingNotOnScheduleTitle")}
                        </Title2>
                        <Body2 className={styles.body}>
                            {strings.get("onboardingFLWCannotCreateScheduleSubtitle")}
                        </Body2>
                    </>
                }
            </>
        );
    };

    const renderActionButtons = (landingPageMode?: LandingPageMode, canCreateTeam?: boolean) => {
        return (
            <>
                {
                    landingPageMode === LandingPageMode.ShowUserOnboardingFLM &&
                    <>
                        {createAndViewScheduleCard()}
                        <Button
                            appearance="transparent"
                            size="medium"
                            className={styles.learnMoreButton}
                            icon={<OpenRegular />}
                            iconPosition="after"
                            onClick={handleLearnMore}
                        >
                            {strings.get("onboardingLearnAboutShifts")}
                        </Button>
                    </>
                }
                {
                    landingPageMode === LandingPageMode.ShowUserOnboardingFLW &&
                    <div className={styles.buttonGroup}>
                        <Button
                            appearance="primary"
                            size="medium"
                            icon={<OpenRegular />}
                            className={styles.primaryActionButton}
                            onClick={handleFindSchedule}
                        >
                            {strings.get("onboardingFindSchedule")}
                        </Button>
                        {
                            canCreateTeam &&
                            <Button
                                appearance="secondary"
                                size="medium"
                                className={styles.actionButton}
                                onClick={handleCreateSchedule}
                            >
                                {strings.get("onboardingCreateScheduleButton")}
                            </Button>
                        }
                        <Button
                            appearance="secondary"
                            size="medium"
                            className={mergeClasses(styles.actionButton, styles.transparentActionButton)}
                            onClick={handleShareShiftsApp}
                        >
                            {strings.get("onboardingShareShiftsApp")}
                        </Button>
                    </div>
                }
            </>
        );
    };

    return (
        <FluentProvider className={styles.root} theme={ThemeUtils.getFluentV2ShiftTheme()}>
            <img
                id="illustration"
                className={styles.illustration}
                src={illustration}
                alt=""
                aria-hidden="true"
            />

            <div className={styles.welcomeCardAndIllustrationBlock}>
                <div className={styles.card}>
                    <div className={styles.textBlock}>
                        {renderTitleBlock(currentLandingPageMode, uiStateCanCreateTeam)}
                    </div>
                    {renderActionButtons(currentLandingPageMode, uiStateCanCreateTeam)}
                </div>
            </div>
        </FluentProvider>
    );
};

export default UserOnboardingWelcome;
